import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BreakdownStateController } from '../../services/breakdown-state.js';
import type { BreakdownHierarchyNode, BreakdownStatus } from '../../services/breakdown-api.js';

const STATUS_OPTIONS: { value: BreakdownStatus; label: string }[] = [
  { value: 'open', label: 'Offen' },
  { value: 'in_progress', label: 'In Arbeit' },
  { value: 'done', label: 'Erledigt' },
  { value: 'skipped', label: 'Uebersprungen' },
];

const PRIORITY_OPTIONS = ['high', 'medium', 'low'];

@customElement('cp-goal-edit-modal')
export class CpGoalEditModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) goal: BreakdownHierarchyNode | null = null;
  @property({ type: Boolean }) open = false;

  @state() private _title = '';
  @state() private _description = '';
  @state() private _priority = 'medium';
  @state() private _status: BreakdownStatus = 'open';
  @state() private _saving = false;
  @state() private _error = '';

  private _breakdown = new BreakdownStateController(this);

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open && this.goal) {
      this._resetForm();
    }
  }

  private _resetForm(): void {
    if (!this.goal) return;
    this._title = this.goal.title;
    this._description = this.goal.description ?? '';
    this._priority = this.goal.priority || 'medium';
    this._status = this.goal.status;
    this._saving = false;
    this._error = '';
  }

  private _close(): void {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this._close();
    }
  };

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-goal-edit-modal__overlay')) {
      this._close();
    }
  }

  private _onTitleInput(e: Event): void {
    this._title = (e.target as HTMLInputElement).value;
  }

  private _onDescriptionInput(e: Event): void {
    this._description = (e.target as HTMLTextAreaElement).value;
  }

  private _onPriorityChange(e: Event): void {
    this._priority = (e.target as HTMLSelectElement).value;
  }

  private _onStatusChange(e: Event): void {
    this._status = (e.target as HTMLSelectElement).value as BreakdownStatus;
  }

  private async _onSave(): Promise<void> {
    if (!this.goal || this._saving) return;

    const trimmedTitle = this._title.trim();
    if (!trimmedTitle) {
      this._error = 'Titel darf nicht leer sein';
      return;
    }

    this._saving = true;
    this._error = '';

    try {
      // Update goal details (title, description, priority)
      const updates: { title?: string; description?: string; priority?: string } = {};
      if (trimmedTitle !== this.goal.title) updates.title = trimmedTitle;
      if (this._description !== (this.goal.description ?? '')) updates.description = this._description;
      if (this._priority !== this.goal.priority) updates.priority = this._priority;

      if (Object.keys(updates).length > 0) {
        await this._breakdown.updateGoal(this.goal.id, updates);
      }

      // Update status if changed
      if (this._status !== this.goal.status) {
        await this._breakdown.updateGoalStatus(this.goal.id, this._status);
      }

      this.dispatchEvent(
        new CustomEvent('goal-saved', {
          detail: { goalId: this.goal.id },
          bubbles: true,
          composed: true,
        }),
      );
      this._close();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (!this.open || !this.goal) return nothing;

    return html`
      <div class="cp-goal-edit-modal__overlay" @click=${this._onBackdropClick}>
        <div class="cp-goal-edit-modal__dialog">
          <div class="cp-goal-edit-modal__header">
            <h2 class="cp-goal-edit-modal__heading">Ziel bearbeiten</h2>
            <button class="cp-goal-edit-modal__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-goal-edit-modal__body">
            <div class="cp-goal-edit-modal__field">
              <label class="cp-goal-edit-modal__label">Titel</label>
              <input
                class="cp-goal-edit-modal__input"
                type="text"
                .value=${this._title}
                @input=${this._onTitleInput}
              />
            </div>

            <div class="cp-goal-edit-modal__field">
              <label class="cp-goal-edit-modal__label">Beschreibung</label>
              <textarea
                class="cp-goal-edit-modal__textarea"
                rows="3"
                .value=${this._description}
                @input=${this._onDescriptionInput}
              ></textarea>
            </div>

            <div class="cp-goal-edit-modal__row">
              <div class="cp-goal-edit-modal__field cp-goal-edit-modal__field--half">
                <label class="cp-goal-edit-modal__label">Prioritaet</label>
                <select
                  class="cp-goal-edit-modal__select"
                  .value=${this._priority}
                  @change=${this._onPriorityChange}
                >
                  ${PRIORITY_OPTIONS.map(p => html`
                    <option value=${p} ?selected=${this._priority === p}>${p}</option>
                  `)}
                </select>
              </div>

              <div class="cp-goal-edit-modal__field cp-goal-edit-modal__field--half">
                <label class="cp-goal-edit-modal__label">Status</label>
                <select
                  class="cp-goal-edit-modal__select"
                  .value=${this._status}
                  @change=${this._onStatusChange}
                >
                  ${STATUS_OPTIONS.map(s => html`
                    <option value=${s.value} ?selected=${this._status === s.value}>${s.label}</option>
                  `)}
                </select>
              </div>
            </div>

            ${this._error ? html`
              <div class="cp-goal-edit-modal__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-goal-edit-modal__footer">
            <button class="cp-goal-edit-modal__btn cp-goal-edit-modal__btn--cancel" @click=${this._close}>Abbrechen</button>
            <button
              class="cp-goal-edit-modal__btn cp-goal-edit-modal__btn--save"
              ?disabled=${this._saving}
              @click=${this._onSave}
            >${this._saving ? 'Speichern...' : 'Speichern'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-goal-edit-modal': CpGoalEditModal;
  }
}
